/**
 * THE ticket-detail wire shapes — what GET /assets/:id/service-calls/:callId answers
 * (server/routes/serviceCalls.js + server/utils/serviceCallContext.js).
 *
 * They live here rather than inside ServiceCallsPage because four surfaces read them now:
 * the page, the three ticket-detail cards, and the per-asset ticket list. Every field is
 * snake_case exactly as the row arrives — no normalizer in between to drift from.
 */

/** One asset the ticket touches — a service_call_pois row, resolved (migration 043). */
export interface TicketAsset {
  poi_id: number;
  title: string | null;
  /** The external POI key (`rvt_4187` / `ifc_<guid>`), not the primary key. */
  poi_key?: string | null;
  ifc_category?: string | null;
  system_category?: string | null;
  system_id?: number | null;
  /** Root→leaf system path, e.g. "HVAC › Chilled Water". */
  system_path?: string | null;
  systems?: { id: number; name: string; path: string }[];
  location_id?: string | null;
  /** Root→leaf location path, e.g. "Building 24 › Basement › Plant room". */
  location_path?: string | null;
  ifc_source_file?: string | null;
  ifc_element_express_id?: number | null;
  ifc_element_guid?: string | null;
  custom_data?: string | null;
  linked_at?: string;
  /** Derived per request from service_calls.context.poiId — never stored. */
  is_primary: boolean;
}

/** One assignee — a user OR a whole group (service_call_assignees.member_type). */
export interface TicketAssignee {
  /** The junction row id; what DELETE .../assignees/:assigneeId addresses. */
  id: number;
  member_type: 'user' | 'group';
  member_id: string;
  name: string | null;
  email?: string | null;
  /** Groups only — counted live, never copied onto the ticket. */
  people_count?: number | null;
  site_count?: number | null;
}

/** One row of the assignee picker: the same two member kinds, bounded by the site. */
export interface AssignableMember {
  member_type: 'user' | 'group';
  member_id: string;
  name: string;
  email?: string | null;
  people_count?: number | null;
  site_count?: number | null;
}

export interface TicketDiagramNode {
  id: number;
  kind: 'asset' | 'system' | 'logical';
  ref_id: string | null;
  label: string;
  pos_x: number;
  pos_y: number;
  state: string;
  /** Set only for nodes that are on the ticket. */
  poi_id: number | null;
  role: 'primary' | 'linked' | null;
}

export interface TicketDiagramEdge {
  id: number;
  source_id: number;
  target_id: number;
  kind: string;
  directed: boolean;
}

/** THE focused diagram: the one holding the primary asset, with the ticket's assets marked. */
export interface TicketDiagram {
  id: number;
  name: string;
  is_canonical: boolean;
  /** How many of the ticket's assets are on this diagram. */
  assets_here: number;
  nodes: TicketDiagramNode[];
  edges: TicketDiagramEdge[];
  /** Ticket assets that sit on a DIFFERENT diagram — offered as a jump, never dropped. */
  elsewhere: { poi_id: number; diagram_id: number; diagram_name: string }[];
}

export interface TicketScope {
  scope_type: 'location' | 'system';
  scope_id: string;
  name: string;
  path?: string | null;
}

export interface TicketQueueState {
  status: string;
  priority: string;
  search: string;
  queue: string;
  assignee: string;
  issueType: string;
  tag: string;
  overdue: boolean;
  sort: string;
  scope: TicketScope | null;
  poiId: string;
  poiName: string;
  page: number;
  selectedId: number | null;
}

export const DEFAULT_TICKET_QUEUE: TicketQueueState = {
  status: 'active', priority: '', search: '', queue: '', assignee: '', overdue: false,
  issueType: '', tag: '', sort: 'newest', scope: null, poiId: '', poiName: '', page: 1, selectedId: null,
};
